import React, { useCallback, useEffect, useState } from "react";
import { ConfirmDialog } from "../redesign/ConfirmDialog";
import { Toast } from "../redesign/Toast";
import { MiniSpinner } from "../chat/chatUi";
import {
    mailVorgangRegelService,
    type MailVorgangRegelDto,
} from "../../services/mailVorgangRegelService";
import { regelDtoKlartext } from "./mailRegel";
import "./mailSettings.css";
import { reportMailError } from "../../services/mailError";

/**
 * Verwaltungsliste der Mail-Regeln (Cluster REGELN, H5) — Bearbeiten,
 * Pausieren, Entfernen.
 *
 * DREI Modi, weil es zwei verschiedene Blickwinkel gibt:
 *  - `eigene`  — „Meine Regeln" in den Mail-Einstellungen. Auch Regeln OHNE
 *    Vorgang (Aktion „neuer Vorgang") sind nur hier auffindbar.
 *  - `vorgang` — ALLE Regeln DIESES Vorgangs, auch die von Kolleginnen. Ohne
 *    diesen Modus wären fremde Regeln an einem Vorgang nirgends mehr
 *    abschaltbar (die Einstellungen filtern hart auf den Ersteller).
 *  - `beide`   — beide Listen, nach Id dedupliziert.
 *
 * Fluent-frei: `.mset-*`-Klassen, `ConfirmDialog` und `Toast` (beide kvp-rd).
 */

type Props = {
    quelle: "eigene" | "vorgang" | "beide";
    /** Pflicht bei `quelle` „vorgang" und „beide". */
    vorgangId?: number | null;
    /** Beschriftung des Vorgangs für den Klartext. */
    vorgangLabel?: string | null;
    /** Zählt hoch, wenn draußen etwas gespeichert wurde → Liste neu laden. */
    reloadKey?: number;
    /** „Bearbeiten" — ohne Handler wird der Knopf nicht angeboten. */
    onEdit?: (regel: MailVorgangRegelDto) => void;
};

export const MailRegelSection: React.FC<Props> = ({
    quelle,
    vorgangId,
    vorgangLabel,
    reloadKey,
    onEdit,
}) => {
    const [rows, setRows] = useState<MailVorgangRegelDto[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);
    const [toast, setToast] = useState<{ msg: string; tone: "error" | "success" } | null>(null);
    const [confirmRow, setConfirmRow] = useState<MailVorgangRegelDto | null>(null);

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            const listen: MailVorgangRegelDto[][] = [];
            if (quelle === "eigene" || quelle === "beide") {
                listen.push(await mailVorgangRegelService.listEigene());
            }
            if ((quelle === "vorgang" || quelle === "beide") && vorgangId) {
                listen.push(await mailVorgangRegelService.list(vorgangId));
            }
            // Nach Id dedupliziert — eine eigene Regel an diesem Vorgang steht
            // sonst zweimal da.
            const byId = new Map<number, MailVorgangRegelDto>();
            for (const liste of listen) for (const r of liste) byId.set(r.id, r);
            setRows(Array.from(byId.values()).sort((a, b) => b.id - a.id));
        } catch (error: unknown) {
            setFehler(reportMailError({
                area: "mail",
                action: "load-settings",
                error,
                fallbackMessage: "Regeln konnten nicht geladen werden.",
            }));
        } finally {
            setLaedt(false);
        }
    }, [quelle, vorgangId]);

    useEffect(() => {
        void laden();
    }, [laden, reloadKey]);

    const umschalten = async (r: MailVorgangRegelDto) => {
        setBusyId(r.id);
        try {
            const neu = await mailVorgangRegelService.setAktiv(r.id, !r.aktiv);
            setRows((prev) => prev.map((x) => (x.id === r.id ? neu : x)));
            setToast({ msg: neu.aktiv ? "Regel aktiviert." : "Regel pausiert.", tone: "success" });
        } catch (error: unknown) {
            setToast({
                msg: reportMailError({
                    area: "mail",
                    action: "save-settings",
                    error,
                    fallbackMessage: "Regel konnte nicht geändert werden.",
                }),
                tone: "error",
            });
        } finally {
            setBusyId(null);
        }
    };

    const entfernen = async (r: MailVorgangRegelDto) => {
        setBusyId(r.id);
        try {
            await mailVorgangRegelService.remove(r.id);
            setRows((prev) => prev.filter((x) => x.id !== r.id));
            setToast({ msg: "Regel entfernt.", tone: "success" });
        } catch (error: unknown) {
            setToast({
                msg: reportMailError({
                    area: "mail",
                    action: "delete-settings",
                    error,
                    fallbackMessage: "Regel konnte nicht entfernt werden.",
                }),
                tone: "error",
            });
        } finally {
            setBusyId(null);
            setConfirmRow(null);
        }
    };

    const titel =
        quelle === "vorgang" ? "Regeln dieses Vorgangs" : quelle === "eigene" ? "Meine Regeln" : "Regeln";

    return (
        <div className="mset-card">
            <h2 className="mset-title">
                {titel} ({rows.length})
            </h2>

            {laedt ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <MiniSpinner />
                    <span style={{ fontSize: 14 }}>Regeln werden geladen …</span>
                </div>
            ) : fehler ? (
                <div className="mset-empty" style={{ color: "var(--mset-err)" }}>
                    {fehler}
                </div>
            ) : rows.length === 0 ? (
                <div className="mset-empty">
                    {quelle === "vorgang"
                        ? "Noch keine Regel für diesen Vorgang."
                        : "Noch keine eigene Regel angelegt."}
                </div>
            ) : (
                rows.map((r) => {
                    const busy = busyId === r.id;
                    const name = r.name?.trim() || regelDtoKlartext(r, vorgangLabel);
                    return (
                        <div key={r.id} className="mset-row" style={{ opacity: r.aktiv ? 1 : 0.6 }}>
                            <div className="mset-row-main">
                                <div className="mset-row-name">{name}</div>
                                <div className="mset-row-meta">{regelDtoKlartext(r, vorgangLabel)}</div>
                                <div className="mset-row-meta" style={{ marginTop: 4 }}>
                                    <span className="mset-badge">
                                        {r.letzteAusfuehrungAt
                                            ? `zuletzt ausgelöst am ${new Date(
                                                  r.letzteAusfuehrungAt,
                                              ).toLocaleDateString("de-DE")}`
                                            : "noch nie ausgelöst"}
                                    </span>
                                    {!r.aktiv && (
                                        <span className="mset-badge warn" style={{ marginLeft: 6 }}>
                                            pausiert
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="mset-row-actions">
                                {onEdit && (
                                    <button
                                        type="button"
                                        className="mset-btn"
                                        disabled={busy}
                                        onClick={() => onEdit(r)}
                                    >
                                        Bearbeiten
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="mset-btn"
                                    disabled={busy}
                                    onClick={() => void umschalten(r)}
                                    title={r.aktiv ? "Regel pausieren" : "Regel wieder aktivieren"}
                                >
                                    {r.aktiv ? "Pausieren" : "Aktivieren"}
                                </button>
                                <button
                                    type="button"
                                    className="mset-btn danger"
                                    disabled={busy}
                                    onClick={() => setConfirmRow(r)}
                                >
                                    Entfernen
                                </button>
                            </div>
                        </div>
                    );
                })
            )}

            <ConfirmDialog
                open={!!confirmRow}
                title="Regel entfernen"
                message={
                    <>
                        Die Regel{" "}
                        <b>
                            {confirmRow
                                ? confirmRow.name?.trim() || regelDtoKlartext(confirmRow, vorgangLabel)
                                : ""}
                        </b>{" "}
                        wird entfernt. Bereits zugeordnete Mails bleiben unberührt.
                    </>
                }
                confirmLabel="Entfernen"
                busy={busyId !== null}
                onConfirm={() => confirmRow && void entfernen(confirmRow)}
                onCancel={() => setConfirmRow(null)}
            />
            <Toast
                open={!!toast}
                message={toast?.msg ?? ""}
                tone={toast?.tone}
                onClose={() => setToast(null)}
            />
        </div>
    );
};

export default MailRegelSection;
